import { Backdrop } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useAiAssistantStore } from '../store/ai-assistant.store'
import type { AiAssistantAction, AiAssistantConversationType, AiAssistantQuickActionLink } from '../model/ai-assistant.model'
import { useUserStore } from 'src/feature/user/store/user.store'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { AssistantComposer } from './AssistantComposer'
import { AssistantConversation } from './AssistantConversation'
import { AssistantHeader } from './AssistantHeader'
import { AssistantLauncher } from './AssistantLauncher'

type QuickActionNavigationState = {
  openCareerGoalModal?: boolean;
  openInterestModal?: boolean;
  openAddSkillModal?: boolean;
};

type QuickActionDestination = {
  path: string
  state?: QuickActionNavigationState
}

const QUICK_ACTION_DESTINATIONS: Record<AiAssistantQuickActionLink, QuickActionDestination> = {
  career_goals: { path: appRoutePath.profileInfo, state: { openCareerGoalModal: true } },
  interests: { path: appRoutePath.profileInfo, state: { openInterestModal: true } },
  unused_skills: { path: appRoutePath.profileInfo, state: { openAddSkillModal: true } },
  skill_assessment: { path: appRoutePath.userDashboard },
  my_team_overview: { path: appRoutePath.myTeam },
  my_team_skill_gaps: { path: appRoutePath.myTeamSkillGaps('') },
  my_team_performance: { path: appRoutePath.myTeamPerformance },
  my_team_performance_create: { path: appRoutePath.myTeamPerformanceCreate },
  my_team_dashboard: { path: appRoutePath.myTeamDashboard },
  project_builder: { path: appRoutePath.projectBuilder },
  users: { path: appRoutePath.controlpanelUsers },
  organisation: { path: appRoutePath.controlpanelOrganisation },
}

export const YohrsAssistant = observer(() => {
  const store = useAiAssistantStore()
  const navigate = useNavigate()
  const { translation, locale } = useLocalizationStore()
  const { avatar, fullName, userData } = useUserStore()
  const aiAssistantTranslation = translation.pages.myDashboard.aiAssistant
  const [input, setInput] = useState('')
  const [isPlatformTopic, setIsPlatformTopic] = useState(false)
  const conversationType: AiAssistantConversationType = isPlatformTopic ? 'platform' : 'skill'
  const messageCount = store.messageCount

  useEffect(() => {
    if (!store.isOpen || !userData?.id) {
      return
    }

    void store.ensureConversation()
  }, [store, store.isOpen, userData?.id])

  const handleSend = () => {
    const trimmed = input.trim()

    if (!trimmed || store.isTyping) {
      return
    }
    setInput('')
    void store.sendMessage(trimmed, conversationType)
  }

  const handlePromptClick = (prompt: string) => {
    if (store.isTyping) {
      return
    }

    setInput('')
    void store.sendMessage(prompt, conversationType)
  }

  const handleActionLinkClick = (action: AiAssistantAction) => {
    if (!action.linkTo) {
      return
    }

    const destination = action.linkTo === 'my_team_skill_gaps' && action.linkParams?.skillName
      ? { path: appRoutePath.myTeamSkillGaps(encodeURIComponent(action.linkParams.skillName)) }
      : QUICK_ACTION_DESTINATIONS[action.linkTo]

    store.close()
    navigate(destination.path, destination.state ? { state: destination.state } : undefined)
  }

  return (
    <>
      {store.isOpen && (
        <Backdrop open onClick={() => store.close()} sx={styles.backdrop} />
      )}

      {store.isOpen && (
        <AppPaper sx={styles.panel}>
          <AssistantHeader
            title={aiAssistantTranslation.title()}
            subtitle={isPlatformTopic
              ? aiAssistantTranslation.platformSubtitle()
              : aiAssistantTranslation.subtitle()}
            switchTopicLabel={aiAssistantTranslation.switchTopic()}
            skillsTopicLabel={aiAssistantTranslation.skillsTopic()}
            platformTopicLabel={aiAssistantTranslation.platformTopic()}
            isPlatformTopic={isPlatformTopic}
            clearChatLabel={aiAssistantTranslation.clearChat()}
            canClearChat={store.messages.length > 0}
            onSwitchTopic={() => setIsPlatformTopic(current => !current)}
            onClearChat={() => store.clearMessages()}
            onClose={() => store.close()}
          />

          <AssistantConversation
            messages={store.messages}
            isTyping={store.isTyping}
            errorMessage={store.errorMessage}
            suggestedPrompts={store.suggestedPrompts}
            actions={store.actions}
            locale={locale}
            avatar={avatar}
            userName={fullName || userData?.firstName}
            labels={{
              thinking: aiAssistantTranslation.thinking(),
              requestFailedTitle: aiAssistantTranslation.requestFailedTitle(),
              retry: aiAssistantTranslation.retry(),
              suggestedPrompts: aiAssistantTranslation.suggestedPromptsLabel(),
              actions: aiAssistantTranslation.actionsLabel(),
              getActionLink: label => translation.pages.myDashboard.quickActions.goToAction(label),
            }}
            onRetry={() => void store.retryLastRequest()}
            onPromptClick={handlePromptClick}
            onActionLinkClick={handleActionLinkClick}
          />

          <AssistantComposer
            input={input}
            placeholder={aiAssistantTranslation.placeholder()}
            disabled={!input.trim() || store.isTyping}
            onInputChange={setInput}
            onSend={handleSend}
          />
        </AppPaper>
      )}

      <AssistantLauncher
        isOpen={store.isOpen}
        title={aiAssistantTranslation.title()}
        messageCount={messageCount}
        onToggle={() => store.toggle()}
      />
    </>
  )
})

const styles = {
  backdrop: {
    zIndex: 1298,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  panel: {
    position: 'fixed',
    bottom: 80,
    right: 25,
    width: { xs: 'calc(100vw - 32px)', sm: 440 },
    height: {
      xs: 'calc(100vh - 112px)',
      sm: 'min(600px, calc(100vh - 120px))',
    },
    maxHeight: 'calc(100vh - 32px)',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '10px',
    overflow: 'hidden',
    zIndex: 1299,
  },
}
